/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
// eslint-disable-next-line import-x/no-named-as-default
import type Litepicker from 'litepicker';
import { waitFor } from '@ember/test-waiters';
import { type IDropdown } from 'ember-bootstrap/components/bs-dropdown';
import { utcDate } from 'uplisting-frontend/utils';

interface IArgs {
  startDate: Date | '-' | undefined;
  endDate?: Date;
  singleMode?: false;

  onDateSelect(date1: Date, date2?: Date): void;
}

interface PickerDateRangeSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiPickerDateComponent extends Component<PickerDateRangeSignature> {
  @service intl!: Services['intl'];

  @cached @tracked picker!: Litepicker;

  @cached @tracked dropdown?: IDropdown;

  @cached
  get startDate(): Date | '-' | undefined {
    return this.args.startDate;
  }

  @cached
  get endDate(): Date | undefined {
    return this.args.endDate;
  }

  @cached
  get singleMode(): boolean {
    return this.args.singleMode ?? true;
  }

  @action
  @waitFor
  async handleSetupPicker(
    dd: IDropdown,
    element: HTMLDivElement,
  ): Promise<void> {
    this.dropdown = dd;

    const module = await import('litepicker/dist/litepicker.umd');

    window.Litepicker = module.Litepicker;

    this.picker = new window.Litepicker({
      element,
      startDate: this.startDate,
      endDate: this.endDate,
      parentEl: element,
      singleMode: this.singleMode,
      lang: this.intl.primaryLocale,
      position: 'top left',
    });

    this.picker.on('selected', (value1, value2) => {
      const params: [Date] | [Date, Date] = [utcDate(value1.dateInstance)];

      if (value2) {
        params.push(utcDate(value2.dateInstance));
      }

      this.args.onDateSelect(...params);

      this.handleWillDestroy();
    });

    this.picker.show();
  }

  @action
  handleWillDestroy(): void {
    this.picker.hide();
    this.picker.destroy();
    this.dropdown?.closeDropdown();
    this.dropdown = undefined;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Picker::Date': typeof UiPickerDateComponent;
  }
}
